"use client";

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, Loader2, Play, Plus, Shield } from 'lucide-react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import {
  isSourceActive,
  sourceDisplayName,
  sourceLogoExtension,
  sourceLogoSlug,
  testSourceIngest,
  type SourceItem,
} from '../api-utils/sources';
import type { SecurityEventItem } from '../api-utils/security_events';
import ErrorState from '../ui/ErrorState';
import { sourceLabel } from '../lib/sourceNames';
import PageBack from '../ui/PageBack';
import { combine } from '../data/queries/combine';
import { useDataClock } from '../data/queries/DataProvider';
import { useSecurityEvents, useSources } from '../data/queries/hooks';
import ConnectSourceModal from '../components/ConnectSourceModal';

/**
 * Series colours, from the design tokens (src/ui/tokens.css): the brand family first (indigo, blue, teal),
 * the same hues softer after them, then slate. Status colours are left out: they mean something elsewhere.
 */
const PIE_COLORS = [
  'var(--acc)',
  'var(--layer-infra)',
  'var(--layer-external)',
  'color-mix(in srgb, var(--acc) 60%, var(--s1))',
  'color-mix(in srgb, var(--layer-infra) 60%, var(--s1))',
  'color-mix(in srgb, var(--layer-external) 60%, var(--s1))',
  'color-mix(in srgb, var(--t3) 80%, var(--s1))',
  'color-mix(in srgb, var(--t3) 60%, var(--s1))',
  'color-mix(in srgb, var(--t3) 42%, var(--s1))',
  'color-mix(in srgb, var(--t3) 28%, var(--s1))',
];

export type SourcesNavIntent = {
  connect?: boolean;
  editId?: string;
  /** The application picked from the menu of available sources, whose name and provider go into the new source. */
  provider?: string;
  name?: string;
} | null;

type SourcesPageProps = {
  onBack: () => void;
  intent?: SourcesNavIntent;
  onIntentConsumed?: () => void;
};

function statusLabel(status: string | undefined): string {
  const value = String(status || 'inactive').toLowerCase();
  if (value === 'active') return 'Connected';
  if (value === 'error') return 'Error';
  return 'Inactive';
}

/** Modifier classes of the shared source tile (c3.css, `.c3-src-tile`): connected is the plain tile. */
function statusTone(status: string | undefined): { tile: string; state: string } {
  const value = String(status || 'inactive').toLowerCase();
  if (value === 'active') return { tile: '', state: '' };
  if (value === 'error') return { tile: 'is-error', state: 'is-error' };
  return { tile: 'is-avail', state: 'is-avail' };
}

function sortSources(a: SourceItem, b: SourceItem): number {
  const rank = (s: SourceItem) => {
    const st = String(s.status || '').toLowerCase();
    if (st === 'active') return 0;
    if (st === 'error') return 1;
    return 2;
  };
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return sourceDisplayName(a).localeCompare(sourceDisplayName(b));
}

function providerKey(raw: string | undefined): string {
  const value = String(raw || '').trim().toLowerCase();
  return value || 'unknown';
}

function displayProvider(key: string): string {
  if (key === 'unknown') return 'Unknown';
  return sourceLabel(key);
}

function buildPieData(events: SecurityEventItem[]): Array<{ name: string; key: string; value: number }> {
  const counts = new Map<string, number>();
  for (const event of events) {
    const key = providerKey(event.provider);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([key, value]) => ({ key, name: displayProvider(key), value }))
    .sort((a, b) => b.value - a.value);
}

const SourcesPage: React.FC<SourcesPageProps> = ({ onBack, intent, onIntentConsumed }) => {
  // The records come from the shared cache and the one refresh clock; Demo reads the invented tenant.
  const { refreshNow } = useDataClock();
  const sourcesRead = useSources();
  const eventsRead = useSecurityEvents();
  const read = combine([sourcesRead, eventsRead]);
  const sourceRows = sourcesRead.data;
  const eventRows = eventsRead.data;
  const sources: SourceItem[] = useMemo(() => (read.failed ? [] : sourceRows ?? []), [read.failed, sourceRows]);
  const events: SecurityEventItem[] = useMemo(() => (read.failed ? [] : eventRows ?? []), [read.failed, eventRows]);
  const loading = read.loading;
  const error = read.error ? 'Could not load the sources' : null;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SourceItem | null>(null);
  const [preset, setPreset] = useState<{ provider: string; name: string } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testTargetName, setTestTargetName] = useState<string | null>(null);

  // After a write: show the latest records and read again now.
  const load = useCallback(async () => {
    await refreshNow();
  }, [refreshNow]);

  // The record being edited follows a fresh read of it.
  useEffect(() => {
    setEditing((prev) => {
      if (!prev?._id) return prev;
      return sources.find((item) => item._id === prev._id) || prev;
    });
  }, [sources]);

  useEffect(() => {
    if (!intent || loading) return;
    if (intent.connect) {
      setEditing(null);
      setPreset(intent.provider ? { provider: intent.provider, name: intent.name || intent.provider } : null);
      setModalOpen(true);
      onIntentConsumed?.();
      return;
    }
    if (intent.editId) {
      const found = sources.find((s) => s._id === intent.editId);
      if (found) {
        setEditing(found);
        setModalOpen(true);
      }
      onIntentConsumed?.();
    }
  }, [intent, loading, sources, onIntentConsumed]);

  const sorted = useMemo(() => [...sources].sort(sortSources), [sources]);
  const pieData = useMemo(() => buildPieData(events), [events]);
  const eventTotal = events.length;
  const connected = sources.filter(isSourceActive).length;
  const errored = sources.filter((s) => String(s.status || '').toLowerCase() === 'error').length;
  const inactive = Math.max(0, sources.length - connected - errored);

  const eventsByProvider = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of pieData) map.set(row.key, row.value);
    return map;
  }, [pieData]);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (source: SourceItem) => {
    setEditing(source);
    setModalOpen(true);
  };

  const runTestIngest = async (source: SourceItem) => {
    if (!source._id) return;
    setTestingId(source._id);
    setTestTargetName(sourceDisplayName(source));
    setTestError(null);
    setTestMessage(null);
    setTestResult(null);
    try {
      const result = await testSourceIngest(source._id);
      setTestResult(JSON.stringify(result.raw, null, 2));
      if (!result.ok) {
        throw new Error(result.lastStatus || 'Test ingest failed');
      }
      setTestMessage(result.lastStatus || 'Test ingest OK');
      await load();
    } catch (e) {
      setTestError(e instanceof Error ? e.message : String(e));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="c3-band !pt-4">
      <PageBack onClick={onBack} />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-5 flex-wrap">
          <h2 className="c3-page-title !mb-0">Sources</h2>
          <button
            type="button"
            className="c3-btn c3-btn-primary c3-btn-sm inline-flex items-center gap-2"
            onClick={openNew}
          >
            <Plus className="h-3.5 w-3.5" />
            Connect new source
          </button>
        </div>
        <div className="text-c3-mute text-sm">
          {loading
            ? 'Loading…'
            : error
              ? null
              : `${connected} active · ${errored} error · ${inactive} inactive · ${eventTotal.toLocaleString()} events`}
        </div>
      </div>
      <p className="c3-page-sub mb-5 max-w-3xl">
        Where each source drops its files, and how many security events each provider has sent.
      </p>

      {error && (
        <ErrorState title={error} onRetry={() => void load()}>
          Nothing was changed. Try again in a moment.
        </ErrorState>
      )}

      {/* A failed read shows only the failure: figures, charts and empty messages would claim there is nothing. */}
      {!error && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,380px)_1fr] gap-5 mb-6">
            <div className="rounded-xl border border-c3-tile-bd bg-c3-tile/40 p-4">
              <h3 className="text-sm font-bold text-c3-white mb-1">Volume by provider</h3>
              <p className="text-xs text-c3-mute mb-3">
                Security events received
              </p>
              {loading ? (
                <p className="text-sm text-c3-mute py-16 text-center">Loading chart…</p>
              ) : pieData.length === 0 ? (
                <p className="text-sm text-c3-mute py-16 text-center">No security events yet</p>
              ) : (
                <div className="relative h-[220px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={66}
                        outerRadius={94}
                        paddingAngle={2}
                        stroke="transparent"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={entry.key}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'var(--s1)',
                          border: '1px solid var(--line)',
                          borderRadius: 'var(--r-ctl)',
                          color: 'var(--t1)',
                          boxShadow: 'var(--shadow-pop)',
                          fontSize: 12,
                        }}
                        formatter={(value, name) => {
                          const n = typeof value === 'number' ? value : Number(value) || 0;
                          const pct = eventTotal > 0 ? Math.round((n / eventTotal) * 100) : 0;
                          return [`${n.toLocaleString()} (${pct}%)`, String(name)];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-semibold leading-none text-c3-white tabular-nums">
                      {eventTotal.toLocaleString()}
                    </span>
                    <span className="mt-1 text-xs text-c3-mute">events</span>
                  </div>
                </div>
              )}
              {!loading && pieData.length > 0 && (
                <ul className="mt-2 space-y-1.5 border-t border-c3-tile-bd pt-3">
                  {pieData.map((row, index) => (
                    <li
                      key={row.key}
                      className="flex items-center justify-between gap-2 text-xs text-c3-mute"
                    >
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ background: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span className="truncate text-c3-white">{row.name}</span>
                      </span>
                      <span className="shrink-0 tabular-nums">
                        <span className="text-c3-white">{row.value.toLocaleString()}</span>
                        <span className="ml-2 inline-block w-9 text-right">
                          {eventTotal > 0 ? Math.round((row.value / eventTotal) * 100) : 0}%
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-c3-white">Configured sources</h3>
                <div className="text-c3-mute text-xs flex items-center gap-3">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-c3-green" /> Active
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-c3-red" /> Error
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-t3" /> Inactive
                  </span>
                </div>
              </div>

              {loading ? (
                <p className="text-sm text-c3-mute">Loading…</p>
              ) : sorted.length === 0 ? (
                <div className="rounded-xl border border-dashed border-c3-tile-bd px-4 py-10 text-center">
                  <p className="text-sm text-c3-mute mb-3">No sources configured yet.</p>
                  <button type="button" className="c3-btn c3-btn-primary c3-btn-sm inline-flex items-center gap-2" onClick={openNew}>
                    <Plus className="h-3.5 w-3.5" />
                    Connect new source
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {sorted.map((source) => {
                    const slug = sourceLogoSlug(source);
                    const tone = statusTone(source.status);
                    const title = sourceDisplayName(source);
                    const provider = providerKey(source.provider || source.candidate_source);
                    const eventCount = eventsByProvider.get(provider) || 0;
                    const subtitle =
                      [source.bucket, source.prefix].filter(Boolean).join('/') ||
                      source.normalize_mode ||
                      '';
                    return (
                      <button
                        type="button"
                        key={source._id}
                        className={`c3-src-tile ${tone.tile}`}
                        title={source.last_status || source.last_error || subtitle}
                        onClick={() => openEdit(source)}
                      >
                        <div className="c3-src-id">
                          <div className="c3-src-logo">
                            {slug ? (
                              <>
                                <img
                                  src={`/sources/${slug}.${sourceLogoExtension(slug)}`}
                                  alt=""
                                  onError={(e) => {
                                    const target = e.currentTarget;
                                    target.style.display = 'none';
                                    const fallback = target.nextElementSibling;
                                    if (fallback) (fallback as HTMLElement).style.display = 'flex';
                                  }}
                                />
                                <span className="c3-src-ini hidden" aria-hidden>
                                  <Shield className="w-5 h-5" />
                                </span>
                              </>
                            ) : (
                              <span className="c3-src-ini flex" aria-hidden>
                                <Shield className="w-5 h-5" />
                              </span>
                            )}
                          </div>
                          <div className="c3-src-ident">
                            <div className="c3-src-name" title={title}>
                              {title}
                            </div>
                            {subtitle && (
                              <div className="c3-src-sub" title={subtitle}>
                                {subtitle}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="c3-src-foot">
                          <span className={`c3-src-status ${tone.state}`}>
                            <span className={`c3-src-dot ${tone.state}`} />
                            {statusLabel(source.status)}
                          </span>
                          <span>
                            {eventCount === 0 ? (
                              <span className="text-c3-mute">No events yet</span>
                            ) : (
                              <>
                                <strong>{eventCount.toLocaleString()}</strong> {eventCount === 1 ? 'event' : 'events'}
                              </>
                            )}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <details className="c3-src-test rounded-xl border border-c3-tile-bd bg-c3-tile/40 p-4 mb-4">
            <summary className="c3-src-test-sum">
              <ChevronRight className="c3-src-test-chev h-4 w-4" aria-hidden />
              <span className="text-sm font-bold text-c3-white">Test ingest</span>
              <span className="text-xs text-c3-mute">
                Checks that the new files of a source can be read. It does not start an analysis.
              </span>
            </summary>
            <div className="mt-3" />

            {sorted.length === 0 ? (
              <p className="text-sm text-c3-mute">Save a source first, then test it here.</p>
            ) : (
              <div className="space-y-2">
                {sorted.map((source) => {
                  const title = sourceDisplayName(source);
                  const pathLabel = source.bucket ? `s3://${source.bucket}/${source.prefix || ''}` : 'No drop zone set';
                  const last = source.last_status || source.last_error || '';
                  const lastOk = last.toUpperCase().startsWith('OK');
                  const lastFail =
                    last.toUpperCase().startsWith('FAILED') || Boolean(String(source.last_error || '').trim());
                  const busy = testingId === source._id;
                  return (
                    <div
                      key={`test-${source._id}`}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-c3-tile-bd bg-s2 px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-c3-white truncate">{title}</span>
                          <span className="text-[12px] text-c3-mute">{source.status || 'inactive'}</span>
                          <span className="text-[12px] text-c3-mute truncate">{source.normalize_mode}</span>
                        </div>
                        <p className="text-[12px] text-c3-mute truncate mt-0.5" title={pathLabel}>
                          {pathLabel}
                        </p>
                        {last && (
                          <p
                            className={`text-[12px] truncate mt-1 ${
                              lastOk ? 'text-c3-green' : lastFail ? 'text-c3-red' : 'text-c3-mute'
                            }`}
                            title={last}
                          >
                            {last}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="c3-btn c3-btn-text c3-btn-sm inline-flex items-center gap-2 shrink-0"
                        disabled={Boolean(testingId) || !source._id}
                        onClick={() => void runTestIngest(source)}
                      >
                        {busy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Play className="h-3.5 w-3.5" />
                        )}
                        Test ingest
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {testError && (
              <div className="mt-3 rounded-lg border border-c3-red/40 bg-c3-red/10 px-3 py-2 text-sm text-c3-red">
                {testTargetName ? `${testTargetName}: ` : ''}
                {testError}
              </div>
            )}
            {testMessage && (
              <div className="mt-3 rounded-lg border border-c3-tile-bd bg-s2 px-3 py-2 text-sm text-c3-green">
                {testTargetName ? `${testTargetName}: ` : ''}
                {testMessage}
              </div>
            )}
            {testResult && (
              <pre className="mt-3 max-h-56 overflow-auto rounded-lg border border-c3-tile-bd bg-s2 p-3 text-[12px] text-c3-mute whitespace-pre-wrap">
                {testResult}
              </pre>
            )}
          </details>
        </>
      )}

      <ConnectSourceModal
        open={modalOpen}
        source={editing}
        preset={preset}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          setPreset(null);
        }}
        onChanged={() => void load()}
      />
    </div>
  );
};

export default SourcesPage;
